/**
 * Folks Cart Drawer — cart-drawer.js
 * Vanilla JS port of the React prototype (cart-app.jsx + all sub-components).
 * No framework dependencies. Integrates with Shopify Section Rendering API.
 */

/* ─── Price formatter (UYU / es-UY locale) ────────────────────────────────── */
const FCD_EMAIL_KEY = 'folks_drawer_email';
const FCD_EMAIL_DRAFT_KEY = 'folks_drawer_email_draft';
const FCD_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function fcdReadStoredEmail() {
  try {
    return (sessionStorage.getItem(FCD_EMAIL_KEY) || '').trim();
  } catch (_) {
    return '';
  }
}

function fcdReadDraftEmail() {
  try {
    return (sessionStorage.getItem(FCD_EMAIL_DRAFT_KEY) || '').trim();
  } catch (_) {
    return '';
  }
}

function fcdStoreEmail(email) {
  try {
    sessionStorage.setItem(FCD_EMAIL_KEY, email);
    sessionStorage.setItem(FCD_EMAIL_DRAFT_KEY, email);
  } catch (_) {}
}

function fcdStoreDraftEmail(email) {
  try {
    sessionStorage.setItem(FCD_EMAIL_DRAFT_KEY, email);
  } catch (_) {}
}

function fcdIsValidEmail(email) {
  return FCD_EMAIL_RE.test(String(email || '').trim());
}

function fcdCheckoutUrlWithEmail(_cart, email) {
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  return `${root}checkout?checkout[email]=${encodeURIComponent(email)}`;
}

function fmtUYU(cents) {
  const n = Math.round(Math.abs(cents)) / 100;
  const int = Math.floor(n);
  const dec = Math.round((n - int) * 100);
  const intStr = int.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return dec > 0
    ? `$${intStr},${String(dec).padStart(2, '0')}`
    : `$${intStr}`;
}

/* ─── AnimatedPrice — cubic-ease-out tween ────────────────────────────────── */
function animatePrice(el, from, to, duration = 420) {
  if (!el) return;
  let raf;
  const start = performance.now();
  const ease = (t) => 1 - Math.pow(1 - t, 3);
  const tick = (now) => {
    const p = ease(Math.min((now - start) / duration, 1));
    el.textContent = fmtUYU(Math.round(from + (to - from) * p));
    if (p < 1) { raf = requestAnimationFrame(tick); }
    else { el.textContent = fmtUYU(to); }
  };
  cancelAnimationFrame(raf);
  raf = requestAnimationFrame(tick);
}

/* ─── RollingNumber — flip animation on qty change ────────────────────────── */
function rollingUpdate(container, newVal, oldVal) {
  if (!container) return;
  const dir = newVal > oldVal ? 1 : -1;
  const a = container.querySelector('.fcd__rolling-a');
  const b = container.querySelector('.fcd__rolling-b');
  if (!a || !b) return;

  /* Position b without triggering CSS transition */
  b.style.transition = 'none';
  b.textContent = newVal;
  b.style.transform = `translateY(${dir * 100}%)`;
  b.style.opacity = '0';

  /* Re-enable transitions then animate */
  requestAnimationFrame(() => {
    b.style.transition = '';
    a.style.transform = `translateY(${-dir * 100}%)`;
    a.style.opacity = '0';
    b.style.transform = 'translateY(0)';
    b.style.opacity = '1';
  });

  setTimeout(() => {
    a.textContent = newVal;
    a.style.transition = 'none';
    a.style.transform = 'translateY(0)';
    a.style.opacity = '1';
    b.style.opacity = '0';
    b.style.transform = `translateY(${dir * 100}%)`;
    /* Restore transition for a */
    requestAnimationFrame(() => { a.style.transition = ''; });
  }, 260);
}

/* ─── Focus trap ──────────────────────────────────────────────────────────── */
function createFocusTrap(container) {
  const FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function getFocusable() {
    return Array.from(container.querySelectorAll(FOCUSABLE))
      .filter((el) => !el.closest('[hidden]') && el.offsetParent !== null);
  }

  function onKeyDown(e) {
    if (e.key !== 'Tab') return;
    const focusable = getFocusable();
    if (!focusable.length) { e.preventDefault(); return; }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  return {
    activate() { container.addEventListener('keydown', onKeyDown); },
    deactivate() { container.removeEventListener('keydown', onKeyDown); },
  };
}

/* ─── Confetti burst ──────────────────────────────────────────────────────── */
function triggerConfetti(shipEl) {
  try {
    if (sessionStorage.getItem('fcd_confetti_once') === '1') return;
    sessionStorage.setItem('fcd_confetti_once', '1');
  } catch (_) {}

  const PALETTE = ['#1B4D35', '#C4956A', '#F4F0E6', '#D4832A', '#4A90D9'];
  const road = shipEl.querySelector('.fcd-ship__road');
  if (!road) return;

  const wrap = document.createElement('div');
  wrap.className = 'fcd-confetti';
  wrap.setAttribute('aria-hidden', 'true');

  const ring = document.createElement('div');
  ring.className = 'fcd-confetti__ring';
  wrap.appendChild(ring);

  for (let i = 0; i < 22; i++) {
    const angle = (i / 22) * Math.PI * 2 + (i % 3);
    const dist = 60 + (i % 5) * 12;
    const cx = Math.cos(angle) * dist;
    const cy = Math.sin(angle) * dist - 20;
    const rot = 180 + (i % 7) * 60;
    const shape = i % 3;
    const size = 5 + (i % 4);
    const delay = (i % 6) * 0.02;
    const color = PALETTE[i % PALETTE.length];

    const chip = document.createElement('span');
    chip.className = 'fcd-confetti__chip';
    chip.style.cssText = `
      width:${size}px;
      height:${shape === 1 ? size * 2 : size}px;
      background:${color};
      border-radius:${shape === 0 ? '50%' : shape === 1 ? '2px' : '1px'};
      animation-delay:${delay}s;
      --cx:${cx}px;
      --cy:${cy}px;
      --cr:${rot}deg;
    `;
    wrap.appendChild(chip);
  }

  road.appendChild(wrap);
  setTimeout(() => wrap.remove(), 1500);
}

/* ─── Shipping bar updater ────────────────────────────────────────────────── */
function updateShippingBar(shipEl, wasUnlockedRef) {
  if (!shipEl) return;

  const subtotal  = parseInt(shipEl.dataset.subtotal,  10) || 0;
  const threshold = parseInt(shipEl.dataset.threshold, 10);
  const alwaysFree = !Number.isFinite(threshold) || threshold <= 0;
  const progress  = alwaysFree ? 1 : Math.min(subtotal / threshold, 1);

  /* Animate fill and truck position */
  requestAnimationFrame(() => {
    const fill  = shipEl.querySelector('[data-fcd-ship-fill]');
    const truck = shipEl.querySelector('[data-fcd-ship-truck]');
    if (fill)  fill.style.width = `${progress * 100}%`;
    if (truck) truck.style.left = `calc(${progress * 100}% - 28px)`;
  });

  shipEl.classList.toggle('fcd-ship--near-complete', progress > 0.8 && progress < 1);

  /* Unlock celebration */
  const isUnlocked = progress >= 1;
  if (isUnlocked && !wasUnlockedRef.val) {
    wasUnlockedRef.val = true;
    shipEl.classList.add('fcd-ship--unlocked');
    triggerConfetti(shipEl);
  } else if (!isUnlocked) {
    wasUnlockedRef.val = false;
    shipEl.classList.remove('fcd-ship--unlocked');
  } else if (isUnlocked) {
    shipEl.classList.add('fcd-ship--unlocked');
  }

  /* Dynamic message */
  const msgEl    = shipEl.querySelector('[data-fcd-ship-msg]');
  const remEl    = shipEl.querySelector('[data-fcd-remaining]');
  const pctEl    = shipEl.querySelector('[data-fcd-progress-pct]');
  const remaining = alwaysFree ? 0 : Math.max(threshold - subtotal, 0);

  if (remEl) remEl.textContent = fmtUYU(remaining);
  if (pctEl) pctEl.textContent = `${Math.round(progress * 100)}%`;

  /* Announce to screen readers */
  return isUnlocked
    ? '¡Sumaste envío gratis!'
    : `Te faltan ${fmtUYU(remaining)} para envío gratis`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   FolksCartDrawer
   ═══════════════════════════════════════════════════════════════════════════ */
class FolksCartDrawer {
  constructor(hostEl) {
    this.host          = hostEl;
    this.dialog        = document.getElementById('folks-cart-drawer');
    this.panel         = document.getElementById('fcd-panel');
    this.liveRegion    = document.getElementById('fcd-live-region');
    this.activeElement = null;
    this.focusTrap     = null;
    this.upsellCache   = { productId: null, html: null };
    this.upsellDismissed = false;
    this.upsellBaseProductId = null;
    this.shippingUnlockedRef = { val: false };
    this.pendingRemoves = [];
    this.undoTimer = null;
    this._removeCommitting = false;
    this.toastTimer = null;
    this._pendingJustAddedHandle = null;
    this._swipeBound = false;

    if (!this.dialog) return;
    this._bindEvents();
    this._bindSwipeClose();
    this._syncEmailGate();

    /* Pre-check if already unlocked on page load */
    const ship = this.dialog.querySelector('.fcd-ship');
    if (ship) {
      const sub = parseInt(ship.dataset.subtotal, 10) || 0;
      const thr = parseInt(ship.dataset.threshold, 10);
      this.shippingUnlockedRef.val = !Number.isFinite(thr) || thr <= 0 || sub >= thr;
    }
  }

  /* ── Event binding (event delegation) ──────────────────────────────────── */
  _bindEvents() {
    /* Direct listener on header cart icon — most reliable, bypasses any stopPropagation */
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (cartIcon) {
      cartIcon.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.open(cartIcon);
        this._refreshFromAPI();
      });
    }

    /* All delegated events on document (capture phase to catch before other handlers) */
    document.addEventListener('click', (e) => {
      /* Close triggers */
      if (e.target.closest('[data-fcd-close]') && this._isOpen()) {
        this.close();
        return;
      }

      /* Open triggers: cart icon (fallback) + <a href="/cart"> links */
      const cartLink = e.target.closest('#cart-icon-bubble, a[href="/cart"], a[href*="/cart"]:not(.fcd *), [data-fcd-open]');
      if (cartLink && !e.target.closest('.fcd')) {
        e.preventDefault();
        this.open(cartLink);
        this._refreshFromAPI();
        return;
      }

      /* Qty buttons */
      const qtyBtn = e.target.closest('[data-fcd-qty]');
      if (qtyBtn) {
        this._handleQty(qtyBtn);
        return;
      }

      /* Remove buttons */
      const removeBtn = e.target.closest('[data-fcd-remove]');
      if (removeBtn) {
        this._handleRemove(removeBtn);
        return;
      }

      /* Checkout CTA */
      if (e.target.closest('[data-fcd-checkout]')) {
        e.preventDefault();
        this._handleCheckout();
        return;
      }

      /* Discount toggle / apply */
      if (e.target.closest('[data-fcd-discount-toggle]')) {
        this._toggleDiscountPanel(e.target.closest('[data-fcd-discount-toggle]'));
        return;
      }
      if (e.target.closest('[data-fcd-discount-apply]')) {
        this._applyDiscount();
        return;
      }

      /* Undo remove */
      if (e.target.closest('[data-fcd-undo]')) {
        this._undoRemove();
        return;
      }
      if (e.target.closest('[data-fcd-undo-dismiss]')) {
        this._dismissUndo();
        return;
      }

      /* Upsell mini product pick */
      const mini = e.target.closest('[data-fcd-upsell-mini]');
      if (mini) {
        this._selectUpsellProduct(mini);
        return;
      }

      /* Upsell add-to-cart */
      const upsellBtn = e.target.closest('[data-fcd-upsell-add]');
      if (upsellBtn) {
        this._handleUpsellAdd(upsellBtn);
        return;
      }

    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-fcd-express]')) return;
      if (fcdIsValidEmail(this._getDrawerEmail())) return;
      e.preventDefault();
      e.stopPropagation();
      this._showEmailError('Ingresá un email para continuar');
    }, true);

    document.addEventListener('input', (e) => {
      const input = e.target.closest?.('[data-fcd-email]');
      if (!input) return;
      this._clearEmailError();
      fcdStoreDraftEmail(input.value.trim());
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      if (!e.target.closest?.('[data-fcd-email]')) return;
      e.preventDefault();
      this._handleCheckout();
    });

    const checkoutForm = document.getElementById('fcd-checkout-form');
    checkoutForm?.addEventListener('submit', (e) => {
      if (e.submitter?.name === 'checkout' || e.submitter?.dataset?.fcdCheckout !== undefined) {
        e.preventDefault();
        this._handleCheckout();
      }
    });

    const discountInput = document.getElementById('fcd-discount-input');
    discountInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this._applyDiscount();
      }
    });

    /* ESC key — let <dialog> fire cancel event */
    this.dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.close();
    });

    /* PubSub: Dawn's product-form.js publishes 'cart-update' after add-to-cart */
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source === 'cart-items') return;
        /* product-form calls renderContents() — only track just-added handle */
        if (event?.source === 'product-form') {
          const added = event.cartData?.items?.[event.cartData.items.length - 1];
          if (added?.handle) this._pendingJustAddedHandle = added.handle;
          return;
        }
        this.open();
        this._refreshFromAPI();
      });
    }
  }

  /* ── Open / Close ──────────────────────────────────────────────────────── */
  open(trigger) {
    this.activeElement = trigger || document.activeElement;
    this.dialog.showModal();
    document.body.style.overflow = 'hidden';
    document.documentElement.dataset.cartOpen = 'true';

    this.focusTrap = createFocusTrap(this.dialog);
    this.focusTrap.activate();

    requestAnimationFrame(() => {
      const closeBtn = this.dialog.querySelector('#fcd-close-btn');
      closeBtn?.focus();
    });

    /* Init dynamic parts */
    this._afterUpdate();
  }

  close() {
    if (!this._isOpen()) return;
    if (this.pendingRemoves.length) this._commitAllRemoves();
    else this._hideUndo();
    this.dialog.classList.add('fcd--closing');

    let closed = false;
    const onEnd = () => {
      if (closed) return;
      closed = true;
      this.dialog.classList.remove('fcd--closing');
      this.dialog.close();
      document.body.style.overflow = '';
      delete document.documentElement.dataset.cartOpen;
      this.focusTrap?.deactivate();
      this.activeElement?.focus();
    };

    /* Listen on panel for animation end */
    const panel = this.dialog.querySelector('.fcd__panel');
    if (panel) {
      panel.addEventListener('animationend', onEnd, { once: true });
      /* Safety fallback: if no animation fires (e.g. reduced-motion) */
      setTimeout(onEnd, 400);
    } else {
      onEnd();
    }
  }

  _isOpen() {
    return this.dialog?.open;
  }

  /* ── Qty / Remove handlers ─────────────────────────────────────────────── */
  _handleQty(btn) {
    const dir     = btn.dataset.fcdQty === 'up' ? 1 : -1;
    const key     = btn.dataset.fcdKey;
    const current = parseInt(btn.dataset.fcdQtyVal, 10) || 1;

    if (dir < 0 && current <= 1) {
      const removeBtn = this.dialog.querySelector(`[data-fcd-remove="${CSS.escape(key)}"]`);
      if (removeBtn) this._handleRemove(removeBtn);
      return;
    }

    const newQty  = current + dir;
    if (newQty < 0) return;

    /* Optimistic rolling number update */
    const rolling = this.dialog.querySelector(`[data-fcd-rolling="${CSS.escape(key)}"]`);
    rollingUpdate(rolling, newQty, current);

    /* Update data attributes on both +/- buttons for this item */
    this.dialog.querySelectorAll(`[data-fcd-key="${CSS.escape(key)}"]`).forEach((b) => {
      b.dataset.fcdQtyVal = newQty;
      /* Optimistically disable minus when qty reaches 1 */
      if (b.dataset.fcdQty === 'down') {
        b.disabled       = newQty <= 1;
        b.ariaDisabled   = newQty <= 1 ? 'true' : 'false';
      }
    });

    /* Optimistic subtotal */
    const itemEl = this.dialog.querySelector(`[data-fcd-key="${CSS.escape(key)}"]`)?.closest('[data-fcd-item]');
    const price  = parseInt(itemEl?.dataset.fcdPrice, 10) || 0;
    this._optimisticSubtotal(price * dir);

    this._changeQty(key, newQty);
  }

  _handleRemove(btn) {
    const key    = btn.dataset.fcdRemove;
    const itemEl = btn.closest('[data-fcd-item]');
    if (!key || !itemEl) return;
    if (this.pendingRemoves.some((p) => p.key === key)) return;

    const qty   = parseInt(itemEl.dataset.fcdQty, 10) || 1;
    const price = parseInt(itemEl.dataset.fcdPrice, 10) || 0;

    itemEl.style.maxHeight = `${itemEl.offsetHeight}px`;
    requestAnimationFrame(() => itemEl.classList.add('fcd__item--removing'));

    this.pendingRemoves.push({ key, qty, price, itemEl });
    const n = this.pendingRemoves.length;
    this._showUndo(n === 1 ? 'Eliminado' : `${n} eliminados`);
    this._scheduleRemoveCommit();
  }

  _scheduleRemoveCommit() {
    clearTimeout(this.undoTimer);
    this.undoTimer = setTimeout(() => this._commitAllRemoves(), 3500);
  }

  async _commitAllRemoves() {
    if (this._removeCommitting || !this.pendingRemoves.length) {
      this._hideUndo();
      return;
    }

    const queue = [...this.pendingRemoves];
    this._clearPendingRemoves();
    this._hideUndo();
    this._removeCommitting = true;

    const updates = {};
    let subtotalDelta = 0;
    queue.forEach(({ key, price, qty }) => {
      updates[key] = 0;
      subtotalDelta -= price * qty;
    });
    this._optimisticSubtotal(subtotalDelta);

    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ updates, sections: 'cart-drawer' }),
      });
      if (!res.ok) throw new Error(`Cart update failed: ${res.status}`);
      const data = await res.json();
      this._updateFromSection(data.sections?.['cart-drawer']);
      this._updateCartBadge(data.item_count);
    } catch (err) {
      console.error('[FolksCart] batch remove', err);
      this._showToast('Error al eliminar productos', 'error');
      await this._refreshFromAPI();
    } finally {
      this._removeCommitting = false;
    }
  }

  _undoRemove() {
    if (!this.pendingRemoves.length) return;
    this.pendingRemoves.forEach(({ itemEl }) => {
      itemEl?.classList.remove('fcd__item--removing');
      if (itemEl) itemEl.style.maxHeight = '';
    });
    this._clearPendingRemoves();
    this._hideUndo();
  }

  _dismissUndo() {
    if (this.pendingRemoves.length) this._commitAllRemoves();
    else this._hideUndo();
  }

  _clearPendingRemoves() {
    clearTimeout(this.undoTimer);
    this.undoTimer = null;
    this.pendingRemoves = [];
  }

  _optimisticSubtotal(delta) {
    const subtotalEl = document.getElementById('fcd-subtotal-display');
    const ctaPriceEl = document.getElementById('fcd-cta-price');
    if (!subtotalEl) return;

    const current  = parseInt(subtotalEl.dataset.subtotal, 10) || 0;
    const newTotal = Math.max(current + delta, 0);

    subtotalEl.dataset.subtotal = newTotal;
    animatePrice(subtotalEl, current, newTotal);
    if (ctaPriceEl) ctaPriceEl.textContent = fmtUYU(newTotal);
  }

  /* ── Email gate ────────────────────────────────────────────────────────── */
  _getDrawerEmail() {
    const input = this.dialog?.querySelector('[data-fcd-email]');
    const typed = (input?.value || '').trim();
    return typed || fcdReadStoredEmail();
  }

  _showEmailError(message) {
    const wrap = this.dialog?.querySelector('[data-fcd-email-wrap]');
    const input = this.dialog?.querySelector('[data-fcd-email]');
    const msg = this.dialog?.querySelector('[data-fcd-email-msg]');
    wrap?.removeAttribute('hidden');
    input?.classList.add('fcd__email-input--error');
    if (msg) {
      msg.textContent = message;
      msg.hidden = false;
    }
    input?.focus();
  }

  _clearEmailError() {
    const input = this.dialog?.querySelector('[data-fcd-email]');
    const msg = this.dialog?.querySelector('[data-fcd-email-msg]');
    input?.classList.remove('fcd__email-input--error');
    if (msg) {
      msg.textContent = '';
      msg.hidden = true;
    }
  }

  _syncEmailGate() {
    const committed = fcdReadStoredEmail();
    const draft = fcdReadDraftEmail();
    const wrap = this.dialog?.querySelector('[data-fcd-email-wrap]');
    const input = this.dialog?.querySelector('[data-fcd-email]');
    const express = this.dialog?.querySelector('[data-fcd-express]');
    if (input && !input.value) input.value = committed || draft;
    const ready = fcdIsValidEmail(committed);
    if (wrap) wrap.hidden = ready;
    if (express) express.hidden = !ready;
  }

  async _createShopifyCustomer(email) {
    const params = new URLSearchParams();
    params.append('form_type', 'customer');
    params.append('utf8', '✓');
    params.append('contact[email]', email);
    params.append('contact[tags]', 'newsletter,cart-save,remarketing,drawer-email');
    await fetch('/contact', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    });
    try {
      if (window.FolksRemarketing && typeof window.FolksRemarketing.identifyEmail === 'function') {
        window.FolksRemarketing.identifyEmail(email, { source: 'cart_drawer' });
      }
      if (window.FolksRemarketing && typeof window.FolksRemarketing.trackSavedCart === 'function') {
        window.FolksRemarketing.trackSavedCart();
      }
    } catch (_) {}
  }

  /* ── Checkout ──────────────────────────────────────────────────────────── */
  async _handleCheckout() {
    const btn = document.getElementById('fcd-checkout-btn');
    if (!btn || btn.disabled) return;

    const email = this._getDrawerEmail();
    if (!fcdIsValidEmail(email)) {
      this._showEmailError('Ingresá un email válido');
      return;
    }

    btn.disabled = true;
    btn.classList.add('fcd__cta--loading');
    const prevHtml = btn.innerHTML;
    btn.innerHTML = `<span class="fcd__spinner" aria-hidden="true"></span> Continuando`;

    try {
      const res  = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const cart = await res.json();

      if (!cart.items?.length) {
        this._showToast('Tu carrito está vacío', 'error');
        btn.disabled = false;
        btn.classList.remove('fcd__cta--loading');
        btn.innerHTML = prevHtml;
        return;
      }

      fcdStoreEmail(email);
      this._clearEmailError();
      try {
        await this._createShopifyCustomer(email);
      } catch (err) {
        console.warn('[FolksCart] customer create', err);
      }

      window.location.href = fcdCheckoutUrlWithEmail(cart, email);
    } catch (err) {
      console.error('[FolksCart] checkout', err);
      this._showToast('No pudimos ir al checkout. Reintentá.', 'error', true, () => this._handleCheckout());
      btn.disabled = false;
      btn.classList.remove('fcd__cta--loading');
      btn.innerHTML = prevHtml;
    }
  }

  /* ── Discount code ─────────────────────────────────────────────────────── */
  _toggleDiscountPanel(btn) {
    const panel = document.getElementById('fcd-discount-panel');
    if (!panel || !btn) return;
    const open = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!open));
    panel.hidden = open;
    if (!open) panel.querySelector('input')?.focus();
  }

  async _applyDiscount() {
    const input = document.getElementById('fcd-discount-input');
    const msgEl = document.getElementById('fcd-discount-msg');
    const applyBtn = document.querySelector('[data-fcd-discount-apply]');
    const code = input?.value?.trim();
    if (!code) {
      if (msgEl) {
        msgEl.hidden = false;
        msgEl.textContent = 'Ingresá un código';
        msgEl.className = 'fcd__discount-msg fcd__discount-msg--error';
      }
      return;
    }

    if (applyBtn) applyBtn.disabled = true;
    try {
      const redirect = encodeURIComponent(window.location.pathname + window.location.search);
      await fetch(`/discount/${encodeURIComponent(code)}?redirect=${redirect}`, {
        method: 'GET',
        credentials: 'same-origin',
      });
      await this._refreshFromAPI();
      if (msgEl) {
        msgEl.hidden = false;
        msgEl.textContent = 'Código aplicado';
        msgEl.className = 'fcd__discount-msg';
      }
      this._showToast('Descuento aplicado', 'success');
      if (input) input.value = '';
    } catch (err) {
      console.error('[FolksCart] discount', err);
      if (msgEl) {
        msgEl.hidden = false;
        msgEl.textContent = 'Código inválido o expirado';
        msgEl.className = 'fcd__discount-msg fcd__discount-msg--error';
      }
      this._showToast('No pudimos aplicar el código', 'error');
    } finally {
      if (applyBtn) applyBtn.disabled = false;
    }
  }

  /* ── Toast / undo UI ───────────────────────────────────────────────────── */
  _showToast(message, type = 'info', showRetry = false, onRetry = null) {
    const el = document.getElementById('fcd-toast');
    if (!el) return;
    clearTimeout(this.toastTimer);
    el.textContent = message;
    el.className = `fcd__toast fcd__toast--visible${type === 'error' ? ' fcd__toast--error' : ''}`;
    el.hidden = false;
    if (showRetry && onRetry) {
      el.style.pointerEvents = 'auto';
      el.style.cursor = 'pointer';
      el.onclick = () => { el.onclick = null; el.style.pointerEvents = ''; onRetry(); };
    } else {
      el.onclick = null;
      el.style.pointerEvents = '';
    }
    this.toastTimer = setTimeout(() => {
      el.classList.remove('fcd__toast--visible');
      setTimeout(() => { el.hidden = true; }, 320);
    }, showRetry ? 5000 : 3200);
  }

  _showUndo(text) {
    if (!this._isOpen()) return;
    const bar = document.getElementById('fcd-undo');
    const label = document.getElementById('fcd-undo-text');
    if (!bar) return;
    if (label) label.textContent = text;
    bar.removeAttribute('hidden');
    bar.classList.add('fcd__undo--visible');
  }

  _hideUndo() {
    const bar = document.getElementById('fcd-undo');
    if (bar) {
      bar.classList.remove('fcd__undo--visible');
      bar.setAttribute('hidden', '');
    }
    clearTimeout(this.undoTimer);
    this.undoTimer = null;
  }

  /* ── Swipe to close (mobile) ───────────────────────────────────────────── */
  _bindSwipeClose() {
    if (this._swipeBound || !this.panel) return;
    this._swipeBound = true;
    let startY = 0;
    let dragging = false;

    const onStart = (e) => {
      if (window.innerWidth >= 640) return;
      const t = e.touches?.[0];
      if (!t) return;
      startY = t.clientY;
      dragging = true;
    };

    const onMove = (e) => {
      if (!dragging) return;
      const t = e.touches?.[0];
      if (!t) return;
      const dy = Math.max(0, t.clientY - startY);
      if (dy > 8) {
        this.panel.classList.add('fcd__panel--dragging');
        this.panel.style.transform = `translateY(${dy}px)`;
      }
    };

    const onEnd = (e) => {
      if (!dragging) return;
      dragging = false;
      const t = e.changedTouches?.[0];
      const dy = t ? Math.max(0, t.clientY - startY) : 0;
      this.panel.classList.remove('fcd__panel--dragging');
      this.panel.style.transform = '';
      if (dy > 100) this.close();
    };

    this.panel.addEventListener('touchstart', onStart, { passive: true });
    this.panel.addEventListener('touchmove', onMove, { passive: true });
    this.panel.addEventListener('touchend', onEnd);
  }

  /* ── Cart API calls ────────────────────────────────────────────────────── */
  async _changeQty(key, qty) {
    try {
      const res  = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: key, quantity: qty, sections: 'cart-drawer' }),
      });
      if (!res.ok) throw new Error(`Cart change failed: ${res.status}`);
      const data = await res.json();
      this._updateFromSection(data.sections?.['cart-drawer']);
      this._updateCartBadge(data.item_count);
    } catch (err) {
      console.error('[FolksCart]', err);
      this._showToast('Error al actualizar el carrito', 'error', true, () => this._changeQty(key, qty));
      this._refreshFromAPI();
    }
  }

  async _addToCart(variantId, quantity = 1, isUpsell = false) {
    const properties = isUpsell ? { _bundle: 'true' } : {};
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity, properties }],
          sections: 'cart-drawer',
        }),
      });
      if (!res.ok) throw new Error(`Cart add failed: ${res.status}`);
      const data = await res.json();
      this._updateFromSection(data.sections?.['cart-drawer']);
      this._updateCartBadge();
    } catch (err) {
      console.error('[FolksCart]', err);
      this._showToast('No pudimos agregar el producto', 'error');
    }
  }

  async _refreshFromAPI() {
    try {
      const res  = await fetch('/?sections=cart-drawer', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      const data = await res.json();
      this._updateFromSection(data['cart-drawer']);
    } catch (err) {
      console.error('[FolksCart] refresh failed', err);
      if (this._isOpen()) this._showToast('No pudimos actualizar el carrito', 'error', true, () => this._refreshFromAPI());
    }
  }

  /* ── Section rendering update ──────────────────────────────────────────── */
  _updateFromSection(sectionHtml) {
    if (!sectionHtml) return;

    const doc        = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newContent = doc.getElementById('fcd-content');
    const current    = document.getElementById('fcd-content');

    if (newContent && current) {
      const scrollEl = current.querySelector('#fcd-scrollable');
      const scrollTop = scrollEl?.scrollTop ?? 0;
      current.innerHTML = newContent.innerHTML;
      const newScroll = current.querySelector('#fcd-scrollable');
      if (newScroll) newScroll.scrollTop = scrollTop;
      this._afterUpdate();
    }
  }

  _afterUpdate() {
    /* Shipping bar */
    const ship = this.dialog?.querySelector('.fcd-ship');
    const msg  = updateShippingBar(ship, this.shippingUnlockedRef);
    if (msg && this.liveRegion) this.liveRegion.textContent = msg;

    /* Subtotal animated price reconcile */
    const subtotalEl = document.getElementById('fcd-subtotal-display');
    if (subtotalEl) {
      const serverVal = parseInt(subtotalEl.dataset.subtotal, 10);
      subtotalEl.textContent = fmtUYU(serverVal);
    }
    const ctaPriceEl = document.getElementById('fcd-cta-price');
    if (ctaPriceEl) {
      const serverVal = parseInt(ctaPriceEl.dataset.subtotal, 10);
      if (serverVal) ctaPriceEl.textContent = fmtUYU(serverVal);
    }

    /* Upsell — reset dismiss when base product changes */
    const upsellEl = document.getElementById('fcd-upsell');
    const baseId = upsellEl?.dataset.productId || null;
    if (baseId && baseId !== this.upsellBaseProductId) {
      this.upsellDismissed = false;
      this.upsellCache = { productId: null, html: null };
      this.upsellBaseProductId = baseId;
    }

    if (this.upsellDismissed) {
      upsellEl?.remove();
    } else {
      this._initUpsell().then(() => this._bindUpsellVariantSelect());
    }

    if (this._pendingJustAddedHandle) {
      this._showJustAdded(this._pendingJustAddedHandle);
      this._scrollToLastItem();
      this._pendingJustAddedHandle = null;
    }

    this._syncEmailGate();
  }

  _scrollToLastItem() {
    const items = this.dialog?.querySelectorAll('[data-fcd-item]');
    const last = items?.[items.length - 1];
    if (last) last.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /* ── Just-added animation ──────────────────────────────────────────────── */
  _showJustAdded(handle) {
    const itemEl = this.dialog?.querySelector(`[data-fcd-handle="${CSS.escape(handle)}"]`);
    if (!itemEl) return;

    const tag = document.createElement('div');
    tag.className = 'fcd__just-added-tag';
    tag.textContent = '¡AGREGADO!';
    tag.setAttribute('aria-live', 'polite');
    itemEl.appendChild(tag);

    setTimeout(() => tag.remove(), 2400);
    itemEl.classList.add('fcd__item--just-added');
    setTimeout(() => itemEl.classList.remove('fcd__item--just-added'), 600);
  }

  /* ── Cart badge ────────────────────────────────────────────────────────── */
  async _updateCartBadge(count) {
    if (count !== undefined) {
      document.querySelectorAll('[data-cart-count], .cart-count-bubble').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
      return;
    }
    /* Fetch current count */
    try {
      const res  = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const data = await res.json();
      this._updateCartBadge(data.item_count);
    } catch (_) {}
  }

  /* ── AI Upsell ─────────────────────────────────────────────────────────── */
  _getCartAnchorTitle() {
    const first = this.dialog?.querySelector('[data-fcd-item] .fcd__item-name');
    return first?.textContent?.trim() || 'tu carrito';
  }

  async _initUpsell() {
    const el = document.getElementById('fcd-upsell');
    if (!el || el.dataset.populated === 'true') return;

    const productId    = el.dataset.productId;
    const upsellHandle = el.dataset.upsellHandle;

    if (!productId && !upsellHandle) { el.remove(); return; }

    if (this.upsellCache.productId === productId && this.upsellCache.html) {
      el.innerHTML = this.upsellCache.html;
      el.dataset.populated = 'true';
      el.setAttribute('aria-busy', 'false');
      this._bindUpsellVariantSelect();
      return;
    }

    let products = [];

    if (productId) {
      try {
        const res  = await fetch(
          `/recommendations/products.json?product_id=${productId}&intent=complementary&limit=3`,
          { headers: { 'X-Requested-With': 'XMLHttpRequest' } }
        );
        const data = await res.json();
        if (data.products?.length) products = data.products;
      } catch (_) {}
    }

    if (!products.length && upsellHandle) {
      try {
        const res  = await fetch(`/products/${upsellHandle}.js`, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        const p = await res.json();
        if (p) products = [p];
      } catch (_) {}
    }

    if (!products.length) {
      el.remove();
      return;
    }

    const cartHandles = Array.from(this.dialog?.querySelectorAll('[data-fcd-item]') || [])
      .map((i) => i.dataset.fcdHandle);
    products = products.filter((p) => !cartHandles.includes(p.handle));
    if (!products.length) {
      el.remove();
      return;
    }

    const html = this._renderUpsellHTML(products);
    this.upsellCache = { productId, html };
    el.innerHTML = html;
    el.dataset.populated = 'true';
    el.setAttribute('aria-busy', 'false');
    this._upsellProducts = products;
  }

  _productImageUrl(product, variant) {
    let src = variant?.featured_image?.src
      || variant?.featured_image
      || product.featured_image
      || product.images?.[0];
    if (src && typeof src === 'object') src = src.src || '';
    if (!src) return '';
    const url = String(src).startsWith('//') ? `https:${src}` : String(src);
    return url.replace(/(\.(jpg|png|webp))(\?.*)?$/i, '_280x280$1$3');
  }

  _renderUpsellHTML(products) {
    const primary = products[0];
    const anchor = this._getCartAnchorTitle();
    const variant = primary.variants?.find((v) => v.available !== false) || primary.variants?.[0];
    if (!variant) return '';

    const price = variant.price;
    const comparePrice = variant.compare_at_price || 0;
    const savings = comparePrice > price ? comparePrice - price : 0;
    const imgUrl = this._productImageUrl(primary, variant);

    const variantSelect = primary.variants?.length > 1
      ? `<select class="fcd-upsell__variant-select" data-fcd-upsell-variant aria-label="Elegir variante">
          ${primary.variants.map((v) => `
            <option value="${v.id}" data-price="${v.price}" data-compare="${v.compare_at_price || 0}"
              ${v.id === variant.id ? 'selected' : ''}
              ${v.available === false ? 'disabled' : ''}>
              ${v.title}${v.available === false ? ' — agotado' : ''}
            </option>`).join('')}
        </select>`
      : '';

    const miniRow = products.length > 1
      ? `<div class="fcd-upsell__row" role="list">
          ${products.map((p, idx) => {
            const v = p.variants?.[0];
            const thumb = this._productImageUrl(p, v);
            return `
            <button type="button" class="fcd-upsell__mini${idx === 0 ? ' fcd-upsell__mini--active' : ''}"
              data-fcd-upsell-mini data-product-index="${idx}" role="listitem">
              ${thumb ? `<img src="${thumb}" alt="" width="64" height="64" loading="lazy">` : ''}
              <span class="fcd-upsell__mini-name">${p.title}</span>
            </button>`;
          }).join('')}
        </div>`
      : '';

    return `
<div class="fcd-upsell" data-fcd-upsell-root data-active-index="0">
  <svg class="fcd-upsell__deco" width="140" height="140" viewBox="0 0 140 140" aria-hidden="true">
    <circle cx="70" cy="70" r="60" fill="none" stroke="#C4956A" stroke-width="1"/>
    <circle cx="70" cy="70" r="40" fill="none" stroke="#C4956A" stroke-width="1"/>
  </svg>

  <div class="fcd-upsell__header">
    <div class="fcd-upsell__ai-label">
      <span class="fcd-upsell__ai-sparkle" aria-hidden="true">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="#1B4D35">
          <path d="M12 1.5l1.8 6.4a3 3 0 0 0 2.3 2.3l6.4 1.8-6.4 1.8a3 3 0 0 0-2.3 2.3L12 22.5l-1.8-6.4a3 3 0 0 0-2.3-2.3L1.5 12l6.4-1.8a3 3 0 0 0 2.3-2.3L12 1.5z"/>
        </svg>
      </span>
      <span class="fcd-upsell__ai-text">RECOMENDADO PARA VOS</span>
    </div>
    ${savings > 0 ? `<div class="fcd-upsell__bundle-badge">AHORRÁ ${fmtUYU(savings)}</div>` : ''}
  </div>

  <div class="fcd-upsell__headline">Combina perfecto<br>con tu rutina.</div>
  <p class="fcd-upsell__reason-line">Elegido para complementar <strong>${anchor}</strong>.</p>

  ${miniRow}

  <div class="fcd-upsell__product" data-fcd-upsell-product>
    <div class="fcd-upsell__img-wrap">
      ${imgUrl ? `<img class="fcd-upsell__img" src="${imgUrl}" alt="${primary.title}" loading="lazy" width="78" height="78">` : ''}
      <div class="fcd-upsell__img-shine" aria-hidden="true"></div>
    </div>
    <div>
      <div class="fcd-upsell__product-name" data-fcd-upsell-title>${primary.title}</div>
      ${variantSelect}
      <div class="fcd-upsell__prices">
        <span class="fcd-upsell__price" data-fcd-upsell-price>${fmtUYU(price)}</span>
        ${comparePrice > price ? `<span class="fcd-upsell__compare" data-fcd-upsell-compare>${fmtUYU(comparePrice)}</span>` : ''}
      </div>
    </div>
  </div>

  <div class="fcd-upsell__cta-wrap">
    <button
      class="fcd-upsell__cta"
      type="button"
      data-fcd-upsell-add
      data-variant-id="${variant.id}"
      data-product-handle="${primary.handle}"
      data-price="${price}"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" stroke-width="2.2"
        stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M12 6v12M6 12h12"/>
      </svg>
      <span data-fcd-upsell-cta-text>AGREGAR · ${fmtUYU(price)}</span>
    </button>
  </div>
</div>`;
  }

  _selectUpsellProduct(miniBtn) {
    const idx = parseInt(miniBtn.dataset.productIndex, 10);
    const products = this._upsellProducts;
    if (!products?.[idx]) return;

    const root = this.dialog.querySelector('[data-fcd-upsell-root]');
    const product = products[idx];
    const variant = product.variants?.find((v) => v.available !== false) || product.variants?.[0];
    if (!variant) return;

    root?.querySelectorAll('[data-fcd-upsell-mini]').forEach((m) => {
      m.classList.toggle('fcd-upsell__mini--active', m === miniBtn);
    });

    const img = root?.querySelector('.fcd-upsell__img');
    const thumb = this._productImageUrl(product, variant);
    if (img && thumb) {
      img.src = thumb;
      img.alt = product.title;
    }

    const titleEl = root?.querySelector('[data-fcd-upsell-title]');
    if (titleEl) titleEl.textContent = product.title;

    const select = root?.querySelector('[data-fcd-upsell-variant]');
    if (select) {
      select.innerHTML = product.variants.map((v) => `
        <option value="${v.id}" data-price="${v.price}" data-compare="${v.compare_at_price || 0}"
          ${v.available === false ? 'disabled' : ''}>${v.title}</option>`).join('');
      select.value = String(variant.id);
    }

    this._syncUpsellVariant(variant.id, variant.price, variant.compare_at_price, product.handle);
    if (root) root.dataset.activeIndex = String(idx);
  }

  _syncUpsellVariant(variantId, price, comparePrice, handle) {
    const root = this.dialog.querySelector('[data-fcd-upsell-root]');
    const btn = root?.querySelector('[data-fcd-upsell-add]');
    const priceEl = root?.querySelector('[data-fcd-upsell-price]');
    const compareEl = root?.querySelector('[data-fcd-upsell-compare]');
    const ctaText = root?.querySelector('[data-fcd-upsell-cta-text]');

    if (btn) {
      btn.dataset.variantId = variantId;
      btn.dataset.productHandle = handle;
      btn.dataset.price = price;
    }
    if (priceEl) priceEl.textContent = fmtUYU(price);
    if (compareEl) {
      if (comparePrice > price) {
        compareEl.textContent = fmtUYU(comparePrice);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
    if (ctaText) ctaText.textContent = `AGREGAR · ${fmtUYU(price)}`;
  }

  _bindUpsellVariantSelect() {
    const select = this.dialog.querySelector('[data-fcd-upsell-variant]');
    if (!select || select.dataset.bound) return;
    select.dataset.bound = 'true';
    select.addEventListener('change', () => {
      const opt = select.selectedOptions[0];
      const btn = this.dialog.querySelector('[data-fcd-upsell-add]');
      const handle = btn?.dataset.productHandle;
      if (!opt) return;
      this._syncUpsellVariant(
        opt.value,
        parseInt(opt.dataset.price, 10),
        parseInt(opt.dataset.compare, 10) || 0,
        handle
      );
    });
  }

  _handleUpsellAdd(btn) {
    if (btn.classList.contains('fcd-upsell__cta--loading')) return;

    const select = this.dialog.querySelector('[data-fcd-upsell-variant]');
    const variantId = select?.value || btn.dataset.variantId;
    const handle = btn.dataset.productHandle;
    if (!variantId) return;

    btn.classList.add('fcd-upsell__cta--loading');
    const ctaText = btn.querySelector('[data-fcd-upsell-cta-text]');
    if (ctaText) ctaText.textContent = 'AGREGANDO…';
    else btn.innerHTML = `<span class="fcd__spinner" aria-hidden="true"></span> AGREGANDO`;

    this.upsellDismissed = true;
    this._pendingJustAddedHandle = handle;

    this._addToCart(variantId, 1, true).then(() => {
      document.getElementById('fcd-upsell')?.remove();
    });
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   CartDrawer custom element — compatibility shim for product-form.js
   ═══════════════════════════════════════════════════════════════════════════ */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    /* Delay to ensure dialog is in DOM */
    requestAnimationFrame(() => {
      window.FolksCart = new FolksCartDrawer(this);
    });
  }
  /** Called by Dawn's product-form.js after successful add-to-cart */
  open(trigger) {
    window.FolksCart?.open(trigger);
  }
  close() {
    window.FolksCart?.close();
  }
  /** Called by Dawn's cart.js to refresh cart contents */
  renderContents(parsedState) {
    if (!parsedState?.sections?.['cart-drawer']) return;
    const items = parsedState.items || parsedState.cart?.items;
    if (items?.length && window.FolksCart) {
      const added = items[items.length - 1];
      if (added?.handle) window.FolksCart._pendingJustAddedHandle = added.handle;
    }
    window.FolksCart?._updateFromSection(parsedState.sections['cart-drawer']);
    window.FolksCart?.open();
    window.FolksCart?._updateCartBadge(parsedState.item_count);
  }
  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#fcd-content' }];
  }
  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML || html;
  }
  setActiveElement(el) {
    if (window.FolksCart) window.FolksCart.activeElement = el;
  }
}

/* Only define if not already registered (e.g. by Dawn's cart-drawer.js) */
if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
} else {
  /* If already defined, patch the existing prototype */
  const proto = customElements.get('cart-drawer').prototype;
  const origConnected = proto.connectedCallback;
  proto.connectedCallback = function () {
    origConnected?.call(this);
    requestAnimationFrame(() => {
      if (!window.FolksCart) {
        window.FolksCart = new FolksCartDrawer(this);
      }
    });
  };
}

/* ─── Also intercept cart-notification opening (cart-notification.js) ─────── */
document.addEventListener('DOMContentLoaded', () => {
  /* Patch any notification that tries to open via old mechanism */
  const notif = document.querySelector('cart-notification');
  if (notif?.open) {
    const orig = notif.open.bind(notif);
    notif.open = function () {
      orig();
      window.FolksCart?.open();
    };
  }

  /* Run shipping bar update on load in case drawer is already open */
  if (document.documentElement.dataset.cartOpen) {
    const ship = document.querySelector('.fcd-ship');
    if (ship) updateShippingBar(ship, { val: false });
  }
});

/* ─── Expose globally for Liquid-rendered onclick hooks if any ────────────── */
window.FolksCartOpen  = () => window.FolksCart?.open();
window.FolksCartClose = () => window.FolksCart?.close();
